import React, { useState, useEffect } from 'react';
import { X, Check, Loader2, FileText } from 'lucide-react';
import lpGenerationService, { LpStyle } from '../../services/api/lpGenerationService';

// 旧 LpStyleSelectModal.scss を内製化（Wave 3d / T20）。
// カードとチェックマークは active / 非 active で重複プロパティを持たない
// 排他的な完全文字列に分けている（SCSS 側は `&.active` が `&:hover` より
// 後ろにあり、hover 中でも active の色が勝っていたため）。

export interface SelectedLpStyle {
  id: number;
  name: string;
  description: string;
  promptTemplate: string;
}

interface LpStyleSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (style: SelectedLpStyle | null) => void;
  currentStyleId?: number | null;
}

const LpStyleSelectModal: React.FC<LpStyleSelectModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  currentStyleId,
}) => {
  const [styles, setStyles] = useState<LpStyle[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<LpStyle | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchStyles();
    } else {
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (currentStyleId && styles.length > 0) {
      const style = styles.find(s => s.id === currentStyleId);
      if (style) {
        setSelectedStyle(style);
      }
    } else {
      setSelectedStyle(null);
    }
  }, [currentStyleId, styles]);

  const fetchStyles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await lpGenerationService.getStyles();
      if (response.success && response.data) {
        setStyles(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch LP styles:', err);
      setError('スタイルの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (style: LpStyle) => {
    setSelectedStyle(style);
  };

  const handleConfirm = () => {
    if (selectedStyle) {
      onSelect({
        id: selectedStyle.id,
        name: selectedStyle.name,
        description: selectedStyle.description,
        promptTemplate: selectedStyle.prompt_template,
      });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-5"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-[640px] flex-col rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.15)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-b-gray-200 px-6 py-5">
          <div className="flex items-center gap-2.5 text-gray-900">
            <FileText size={20} />
            <h2 className="text-[18px] font-semibold">LPスタイルを選ぶ</h2>
          </div>
          <button
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border-none bg-gray-100 text-gray-500 transition-all duration-150 ease-[ease] hover:bg-gray-200 hover:text-gray-700"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 px-5 py-12 text-gray-400">
              <Loader2 size={32} className="animate-spin" />
              <p className="text-[14px]">スタイルを読み込み中...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 px-5 py-12 text-red-500">
              <p className="text-[14px]">{error}</p>
              <button
                onClick={fetchStyles}
                className="cursor-pointer rounded-lg border border-gray-300 bg-gray-100 px-4 py-2 text-[13px] text-gray-700 hover:bg-gray-200"
              >
                再試行
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              {styles.map(style => (
                <button
                  key={style.id}
                  className={
                    selectedStyle?.id === style.id
                      ? 'active flex w-full cursor-pointer items-start gap-3 rounded-xl border-2 border-violet-600 bg-violet-50 p-4 text-left shadow-[0_0_0_1px_#7c3aed] transition-all duration-150 ease-[ease]'
                      : 'flex w-full cursor-pointer items-start gap-3 rounded-xl border-2 border-gray-200 bg-white p-4 text-left transition-all duration-150 ease-[ease] hover:border-violet-400 hover:bg-purple-50'
                  }
                  onClick={() => handleSelect(style)}
                >
                  <div
                    className={
                      selectedStyle?.id === style.id
                        ? 'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[50%] border-2 border-violet-600 bg-violet-600 text-white transition-all duration-150 ease-[ease]'
                        : 'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[50%] border-2 border-gray-300 transition-all duration-150 ease-[ease]'
                    }
                  >
                    {selectedStyle?.id === style.id && <Check size={16} />}
                  </div>
                  {style.thumbnail_url ? (
                    <img
                      className="h-16 w-16 shrink-0 rounded-lg object-cover"
                      src={style.thumbnail_url}
                      alt={style.name}
                    />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-400">
                      <FileText size={24} />
                    </div>
                  )}
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="text-[15px] font-semibold text-gray-900">{style.name}</span>
                    <span className="text-[13px] leading-[1.4] text-gray-500">{style.description}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2.5 border-t border-t-gray-200 px-6 py-4">
          <button
            className="cursor-pointer rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-[14px] font-medium text-gray-700 transition-all duration-150 ease-[ease] hover:bg-gray-100"
            onClick={onClose}
          >
            キャンセル
          </button>
          <button
            className="flex cursor-pointer items-center gap-1.5 rounded-lg border-none bg-violet-600 px-5 py-2.5 text-[14px] font-medium text-white transition-all duration-150 ease-[ease] hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            onClick={handleConfirm}
            disabled={!selectedStyle}
          >
            <Check size={16} />
            選択する
          </button>
        </div>
      </div>
    </div>
  );
};

export default LpStyleSelectModal;
